<template>
  <Layout>
    <div class="mc-report__expenses">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McListDownloader
              :pageOptions="downloadPageOptions"
              :fileFormatOptions="['excel']"
              @download="handleDownload"
            />
            <McButtonBack />
          </div>
        </template>
      </PageHeader>
      <McTable
        withCard
        :searchable="false"
        hasViewManager
        :fields="fields"
        :apiClient="apiClient"
        :hasCheckbox="false"
        :activeFilters="activeFilters"
      >
        <template #before-table>
          <div class="d-flex justify-content-between mb-3">
            <div class="mc-report-period">
              <div class="__label">
                {{ $t('report.object.reportPeriod') }}
              </div>
              <div class="__detail">
                {{ momentMY(period) }}
              </div>
            </div>
            <McTableFilter
              ref="fleetUtilizationFilter"
              :title="$t('report.filters.reportParameter')"
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              :hasResetFilters="false"
              hasCustomEvent
              @submit="handleChangeActiveFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilter
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>
          </div>
        </template>
        <template #cell()="{ field, value }">
          {{ formatValues(field.key, value) }}
        </template>
      </McTable>
    </div>
  </Layout>
</template>

<script>
import omitBy from 'lodash/omitBy'

import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2.vue'
import McButtonBack from '@/components/McButtonBack'
import McTable from '@/components/McTable'
import McListDownloader from '@/components/McListDownloader'
import McTableFilter from '@/components/McTableFilter'
import * as Report from '@/services/rest-api/lib/report/report.js'
import { formatIndexDate } from '@/helpers/tzDate'

import getFields from './lib/fields/fields'
import { formatValues } from './lib/helpers/formatValues'
import InputTableFilter from './components/InputTableFilter'
import parseFilterDataMappings from './lib/constants/parseFilterDataMappings'
import moment from 'moment'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Report Fleet Utilization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    McTable,
    PageHeader,
    McButtonBack,
    McTableFilter,
    McListDownloader,
    InputTableFilter,
  },
  mixins: [Helper],
  data() {
    return {
      params: {},
      metadata: null,
      activeFilters: {},
      finalFilters: {},
    }
  },
  computed: {
    ...mapGetters({ hasAccess: 'identity/hasAccess', isParent: 'identity/isParent' }),
    title() {
      return this.$t('report.title.fleetUtilization')
    },
    apiClient() {
      const queryParamFilters = this.getQueryParamFilters()
      const getReportFleetUtilization = Report.getReportFleetUtilization(
        queryParamFilters,
        this.handleSetParams,
        this.handleSetMetadata
      )
      return {
        get: getReportFleetUtilization,
      }
    },
    fields() {
      return getFields(this).filter((field) => field.isShow)
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    period() {
      const fleetUtilizationFilter = this.$store.state.report.fleetUtilizationFilter
      return fleetUtilizationFilter.created_on
    },
  },
  created() {
    this.updateFilters()
  },
  mounted() {
    this.$nextTick(() => {
      const mcTableFilter = this.$refs.fleetUtilizationFilter

      const fleetUtilizationFilter = this.$store.state.report.fleetUtilizationFilter
      const fleetUtilizationFullObjects = this.$store.state.report.fleetUtilizationFullObjects

      for (let [objectName, value] of Object.entries(fleetUtilizationFullObjects)) {
        mcTableFilter.updateFullObjects({ key: objectName, value })
        mcTableFilter.oldFullObjects[objectName] = value
      }
      for (let [filterName, value] of Object.entries(fleetUtilizationFilter)) {
        mcTableFilter.updateFormValues({ key: filterName, value })
        this.activeFilters[filterName] = value
      }
    })
  },
  methods: {
    formatIndexDate,
    formatValues,
    handleDownload(format, dataAmount) {
      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      const queries = this.queryParamsToString(params)

      window.open(`${process.env.VUE_APP_API_ORDER_URL}/report/5?${queries}`, '_self')
    },
    handleSetParams(params) {
      this.params = params
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
    handleChangeActiveFilters(filters) {
      const mcTableFilter = this.$refs.fleetUtilizationFilter

      const createdOn = filters.find((filter) => filter.key === 'created_on')
      const companyId = filters.find((filter) => filter.key === 'company_id')
      if (!createdOn.values) {
        this.$mcToast.error(
          `${this.$t('report.object.reportPeriod')} ${this.$t('report.filters.required')}`
        )
        mcTableFilter.showModal = true
        return
      }
      if (
        this.isParent() &&
        this.hasAccess(['a10e-report-child-monitoring:read']) &&
        companyId.values.length === 0
      ) {
        this.$mcToast.error(
          `${this.$t('setting.list.managementAsset.subdomain.domain')} ${this.$t(
            'report.filters.required'
          )}`
        )
        mcTableFilter.showModal = true
        return
      }
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined) return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
      mcTableFilter.showModal = false
      this.generatePeriod()
      this.updateFilters()
      this.$store.commit('report/updateFleetUtilizationFullObjects', mcTableFilter.fullObjects)
    },
    generatePeriod() {
      const filters = {}
      for (const [key, val] of Object.entries(this.parseFilterDataMappings)) {
        let value = ''
        if (Array.isArray(val)) {
          value = []
          val.forEach((filter) => {
            value.push(this.activeFilters[filter])
          })
        }
        if (typeof val === 'string') {
          value = this.activeFilters[val]
        }
        filters[key] = value
      }
      this.$store.commit('report/updateFleetUtilizationFilter', filters)
    },
    updateFilters() {
      const fleetUtilizationFilter = this.$store.state.report.fleetUtilizationFilter
      for (let [filterName, value] of Object.entries(fleetUtilizationFilter)) {
        this.activeFilters[filterName] = value
      }
      this.finalFilters = this.activeFilters
      if (Object.keys(this.activeFilters).length === 0) {
        const fleetUtilizationFilter = this.$store.state.report.fleetUtilizationFilter

        const activeFilters = {}
        for (let [filterName, value] of Object.entries(fleetUtilizationFilter)) {
          // assign filters to activeFilters variable
          this.parseFilterDataMappings[filterName].forEach((filter, index) => {
            activeFilters[filter] = value[index]
          })
        }
        this.finalFilters = activeFilters
      }
    },
    getQueryParamFilters() {
      const queryParamFilters = []

      if (this.finalFilters.created_on) {
        const startDate = moment(this.finalFilters.created_on).startOf('month').utc().format()
        const endDate = moment(this.finalFilters.created_on).endOf('month').utc().format()

        queryParamFilters.push(`created_on gte ${startDate}`)
        queryParamFilters.push(`created_on lte ${endDate}`)
      }

      if (this.finalFilters.company_id) {
        const companyIds = this.finalFilters.company_id.join(' ')
        queryParamFilters.push(`company_id in ${companyIds}`)
      }

      return queryParamFilters
    },
  },
}
</script>
